import { useEffect, useMemo } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check } from "lucide-react";
import { formatCents } from "@/lib/money";
import {
  paidPeriodMonthLabel,
  type PaidCelebration as PaidCelebrationData,
} from "@/lib/paid-celebration";

/**
 * COL-103 — the pop-up a tenant gets when a rent or deposit payment flips to
 * paid. Purely presentational: `usePaidCelebration` decides *whether* and the
 * dashboard decides *where*.
 *
 * It is a status announcement, not a dialog: it steals no focus and traps
 * none, because nothing behind it is disabled and the tenant may well be
 * mid-scroll. Any click (and Escape) dismisses it, and it leaves on its own
 * after DISMISS_AFTER_MS.
 *
 * Under `prefers-reduced-motion` it does not appear at all. A quieter version
 * was possible — the same card without the confetti — but this whole surface
 * exists to make a moment of something; a tenant who has asked for less
 * movement is better served by the paid badge updating silently, which it
 * does either way.
 */

const DISMISS_AFTER_MS = 3500;
const CONFETTI_COUNT = 28;

// Teal is the tenant dashboard's accent (the card top rule uses the same hue);
// amber and blue only join in so the burst does not read as one solid blob.
const CONFETTI_COLORS = [
  "hsl(173,65%,42%)",
  "hsl(173,65%,58%)",
  "hsl(38,86%,55%)",
  "hsl(212,72%,54%)",
];

type Props = {
  celebration: PaidCelebrationData | null;
  language: string;
  onDismiss: () => void;
};

function labels(language: string) {
  const de = language === "de";
  return {
    rentTitle: de ? "Miete bezahlt" : "Rent paid",
    depositTitle: de ? "Kaution bezahlt" : "Deposit paid",
    depositSub: de
      ? "Für Ihren Aufenthalt verwahrt"
      : "Held safely for your stay",
    dismiss: de ? "Zum Schließen tippen" : "Tap to close",
    more: (n: number) =>
      de
        ? `+ ${n} weitere ${n === 1 ? "Zahlung" : "Zahlungen"} bezahlt`
        : `+ ${n} more payment${n === 1 ? "" : "s"} paid`,
  };
}

export default function PaidCelebration({
  celebration,
  language,
  onDismiss,
}: Props) {
  // `useReducedMotion` can report null before it has read the media query;
  // only an explicit true suppresses.
  const suppressed = useReducedMotion() === true;
  const l = labels(language);

  useEffect(() => {
    if (!celebration) return;
    // Nothing is rendered when suppressed, so clear the pending celebration
    // immediately rather than leaving it parked in state for 3.5s.
    if (suppressed) {
      onDismiss();
      return;
    }
    // A payment can flip to paid (realtime, or the 30s payments poll) while
    // the dashboard tab is in the background. A plain wall-clock timer would
    // dismiss (and mark seen) a celebration the tenant never actually saw, so
    // the countdown only runs while the tab is visible, and restarts in full
    // once the tenant comes back to it.
    let timer: number | undefined;
    const start = () => {
      timer = window.setTimeout(onDismiss, DISMISS_AFTER_MS);
    };
    const stop = () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timer = undefined;
      }
    };
    const onVisibilityChange = () => {
      if (document.hidden) stop();
      else start();
    };
    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibilityChange);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [celebration, onDismiss, suppressed]);

  // Re-rolled per celebration so two bursts in a row do not look identical,
  // and memoised so a re-render mid-flight does not restart the pieces.
  const confetti = useMemo(() => {
    if (!celebration || suppressed) return [];
    return Array.from({ length: CONFETTI_COUNT }, (_, i) => {
      const angle = (i / CONFETTI_COUNT) * Math.PI * 2 + Math.random();
      const radius = 70 + Math.random() * 140;
      return {
        id: i,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
        x: Math.cos(angle) * radius,
        y: Math.sin(angle) * radius + 120,
        rotate: Math.random() * 720 - 360,
        duration: 0.9 + Math.random() * 0.5,
      };
    });
  }, [celebration, suppressed]);

  const isDeposit = celebration?.kind === "deposit";
  const title = isDeposit ? l.depositTitle : l.rentTitle;
  const amount =
    celebration?.amountCents == null
      ? null
      : formatCents(celebration.amountCents);
  const period = isDeposit
    ? l.depositSub
    : paidPeriodMonthLabel(celebration?.dueDate ?? null, language);
  const subtitle = [amount, period].filter(Boolean).join(" · ");

  return (
    <AnimatePresence>
      {celebration && !suppressed && (
        <motion.div
          key={celebration.paymentId}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onDismiss}
          role="status"
          aria-live="polite"
        >
          <motion.div
            className="relative rounded-2xl bg-card px-8 py-7 text-center shadow-lg"
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ type: "spring", stiffness: 320, damping: 22 }}
          >
            {confetti.map((piece) => (
              <motion.span
                key={piece.id}
                aria-hidden="true"
                className="pointer-events-none absolute left-1/2 top-1/3 h-3 w-[7px] rounded-[1px]"
                style={{ backgroundColor: piece.color }}
                initial={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
                animate={{
                  opacity: 0,
                  x: piece.x,
                  y: piece.y,
                  rotate: piece.rotate,
                }}
                transition={{ duration: piece.duration, ease: "easeOut" }}
              />
            ))}

            <motion.div
              className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full"
              style={{ background: "hsl(173,65%,42%,0.12)" }}
              initial={{ scale: 0.4 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 420,
                damping: 16,
                delay: 0.06,
              }}
            >
              <Check
                className="h-7 w-7"
                style={{ color: "hsl(173,65%,42%)" }}
              />
            </motion.div>

            <p className="text-lg font-semibold text-foreground">{title}</p>
            {subtitle && (
              <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
            )}
            {celebration.additionalCount > 0 && (
              <p className="mt-1 text-sm text-muted-foreground">
                {l.more(celebration.additionalCount)}
              </p>
            )}
            <p className="mt-4 text-xs text-muted-foreground">{l.dismiss}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
